import type { AnySceneObjectT } from "tweenly"
import { Box, CircularProgress } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useLayoutEffect, useState } from "react"
import { selectUndoable } from "../../../helpers/selector.helpers"
import { useCanvasTimer } from "../../../hooks/editor/useCanvasTimer"
import { useCanvas } from "../../../hooks/useCanvas"
import useDynamicRefs from "../../../hooks/useDynamicRefs"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux"
import { clearSelectedAnimationsAction } from "../../../store/slices/activeAnimation.slice"
import {
    clearAllSelectedObjectsAction,
    selectHasMultiSelection,
} from "../../../store/slices/activeObject.slice"
import { selectSortedRootObjects } from "../../../store/slices/objects.slice"
import CanvasItem from "./CanvasItem"
import ObjectsWrapper from "./ObjectsWrapper"
import SelectionOverlay from "./SelectionOverlay"
import SnapGuides from "./SnapGuides"

interface Props {
    size: {
        width: number
        height: number
    }
    zoomScale: number
}

const Canvas = ({ size, zoomScale }: Props) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { canvasResolution: canvas } = useCanvas()
    const sortedObjects: AnySceneObjectT[] = useAppSelector((state) =>
        selectSortedRootObjects(selectUndoable(state).objects)
    )
    const hasMultiSelection = useAppSelector(selectHasMultiSelection)

    // two booleans instead of the whole editor slice — Canvas is the root of the object tree,
    // so anything that re-renders it walks the whole canvas
    const canvasOverflowOn = useAppSelector((state) => state.editor.value.settings.canvasOverflowOn)
    const canvasGridOn = useAppSelector((state) => state.editor.value.settings.canvasGridOn)

    const [canvasScaleCoef, setCanvasScaleCoef] = useState<number>(1)
    const [, setRef] = useDynamicRefs<HTMLDivElement>()

    const borderCoef: number = 2 * (1 / (canvasScaleCoef * zoomScale)) // 2px = default border size

    useLayoutEffect(() => {
        setCanvasScaleCoef(size.width / canvas.width)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size.width])

    const { setActiveObjectId } = useObjectActions()

    //unselect objects and animation when click into canvas
    const handleClick = () => {
        // no drag-click guard here: the click that ends a drag never reaches React at all,
        // it is stopped in the capture phase — see dragClick.helpers
        setActiveObjectId("", [])
        dispatch(clearAllSelectedObjectsAction())
        dispatch(clearSelectedAnimationsAction())
    }

    const pendingPreloadAssets = useAppSelector((state) => state.assets.pendingPreloadAssets)
    // reads the objects from the store on its own — see useCanvasTimer
    useCanvasTimer()

    return (
        <Box
            id="editor-step-2"
            style={{
                position: "relative",
                width: canvas.width + "px",
                height: canvas.height + "px",
                transform: "scale(" + canvasScaleCoef + ")",
                transformOrigin: "top left",
                overflow: canvasOverflowOn ? "visible" : "hidden",
                outline: `${borderCoef}px solid ${theme.palette.divider}`,
                backgroundImage: canvasGridOn
                    ? "linear-gradient(to right, rgba(40, 40, 40, 0.95), rgba(40, 40, 40, 0.95)),linear-gradient(to top, black 50%, white 50%),linear-gradient(to right, black 50%, white 50%)"
                    : "",
                backgroundSize: "40px 40px",
                backgroundPosition: "center",
                backgroundBlendMode: "normal, difference, normal",
            }}
        >
            {pendingPreloadAssets && (
                <Box
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0,0,0,0.45)",
                        zIndex: 10,
                    }}
                >
                    <CircularProgress size={48} />
                </Box>
            )}
            <div
                className="canvas"
                id="canvas"
                ref={setRef("canvas")}
                style={{
                    width: canvas.width + "px",
                    height: canvas.height + "px",
                    transformOrigin: "top left",
                    overflow: canvasOverflowOn ? "visible" : "hidden",
                    outline: `${borderCoef}px solid ${theme.palette.divider}`,
                    backgroundColor: "rgba(0,0,0,0)",
                }}
                onClick={handleClick}
            >
                {/* Every object renders in its natural place in the tree, always. A
                    multiselection used to move the selected ones into ObjectsWrapper, which
                    lifted them to z-index 2500 and so in front of the whole scene. */}
                {sortedObjects.map((object: AnySceneObjectT) => (
                    <CanvasItem
                        key={object.id}
                        object={object}
                        canvasScaleCoef={canvasScaleCoef}
                        isHiding={false}
                        isLocking={false}
                        isRootTicker={false}
                        borderCoef={borderCoef}
                    />
                ))}

                {/* behaviour only, renders nothing — see ObjectsWrapper */}
                {hasMultiSelection && <ObjectsWrapper canvasScaleCoef={canvasScaleCoef} />}

                {/* last child, so the selection chrome paints above every object — see the
                    stacking-context note in SelectionOverlay */}
                <SelectionOverlay canvasScaleCoef={canvasScaleCoef} zoomScale={zoomScale} />

                {/* snap-to-objects alignment lines, only while a drag has snapped */}
                <SnapGuides canvasScaleCoef={canvasScaleCoef} zoomScale={zoomScale} />
            </div>
        </Box>
    )
}

export default Canvas
